import { Box, Checkbox, FormControlLabel, FormGroup, FormLabel, Stack } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import * as state from '@/components/AssetLabelling/state';
import { notEmpty } from '@/utils';
import { useCallback, useMemo } from 'react';
import { LabelClass } from '@/__generated__/graphql';

export const PointLabels = () => {
  const structure = useAtomValue(state.structure);
  const [labelClassById, setLabelClassById] = useAtom(state.labelClassById);

  const labelClasses = useMemo(() => structure?.labelClasses?.filter(notEmpty), [structure]);

  const isVisible = useCallback(
    (labelClassId: string) => {
      if (!labelClassById) return true;
      return labelClassById[labelClassId].isVisible ?? true;
    },
    [labelClassById]
  );

  const toggleIsVisible = useCallback(
    (labelClassId: string) => {
      setLabelClassById((current) => {
        if (!current) return undefined;
        const entry = current[labelClassId];
        const updatedEntry = { ...entry, isVisible: !entry.isVisible };
        return { ...current, [labelClassId]: updatedEntry };
      });
    },
    [setLabelClassById]
  );

  const isAllVisible = useCallback(() => {
    const hasInvisibleItems = labelClasses?.some(
      ({ id }) => !(labelClassById?.[id].isVisible ?? true)
    );
    return !hasInvisibleItems;
  }, [labelClassById, labelClassById]);

  const isAllInvisible = useCallback(() => {
    const hasVisibleClasses = labelClasses?.some(
      ({ id }) => labelClassById?.[id].isVisible ?? true
    );
    return !hasVisibleClasses;
  }, [labelClassById, labelClassById]);

  const toggleIsAllVisible = useCallback(() => {
    if (!labelClassById) return;
    const newIsVisible = !isAllVisible();
    setLabelClassById((current) => {
      if (!current) return current;
      const newLabelClassById: { [id: string]: LabelClass & { isVisible: boolean } } = {};
      labelClasses?.forEach(({ id }) => {
        newLabelClassById[id] = { ...current[id], isVisible: newIsVisible };
      });
      return newLabelClassById;
    });
  }, [labelClasses, labelClassById, isAllVisible, setLabelClassById]);

  return (
    <Stack direction="column">
      <FormGroup>
        <FormLabel sx={{ padding: '15px 0px 5px' }}>Point Labels</FormLabel>
        <Stack direction="row" alignItems="center" key="all-cuboid-classes-checkbox">
          <Box
            style={{
              width: '20px',
              height: '20px',
              margin: '5px 10px',
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAllVisible()}
                indeterminate={!isAllVisible() && !isAllInvisible()}
                onChange={() => toggleIsAllVisible()}
              />
            }
            label="All labels"
          />
        </Stack>
        {labelClasses?.map(({ id, name, color }) => (
          <Stack direction="row" alignItems="center" key={id}>
            <Box
              style={{
                backgroundColor: color,
                width: '20px',
                height: '20px',
                border: '1px solid #555',
                borderRadius: '10px',
                margin: '5px 10px',
              }}
            />

            <FormControlLabel
              control={<Checkbox checked={isVisible(id)} onChange={() => toggleIsVisible(id)} />}
              label={name}
            />
          </Stack>
        ))}
      </FormGroup>
    </Stack>
  );
};
